import {
  CancelOutlined,
  CheckCircleOutline,
  ErrorOutline,
} from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import type { PasswordValidationStatus } from 'firebase/auth'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'react-hook-form'
import {
  RequirementItem,
  RequirementsList,
} from './PasswordRequirements.styles'
import { getStandardPluralLabel } from 'shared/utils/plurals'

interface PasswordRequirementsProps {
  passwordName: string
  repeatPasswordName: string
  status: PasswordValidationStatus | null
  validating: boolean
  validationFailed: boolean
}

interface Requirement {
  key: string
  label: string
  fulfilled: boolean
}

const PasswordRequirements = ({
  passwordName,
  repeatPasswordName,
  status,
  validating,
  validationFailed,
}: PasswordRequirementsProps) => {
  const { t, i18n } = useTranslation()

  const [password = '', repeatPassword = ''] = useWatch({
    name: [passwordName, repeatPasswordName],
  }) as [string, string]

  const requirements: Requirement[] = []

  if (status) {
    const {
      minPasswordLength,
      maxPasswordLength,
      containsLowercaseLetter,
      containsUppercaseLetter,
      containsNumericCharacter,
      containsNonAlphanumericCharacter,
    } = status.passwordPolicy.customStrengthOptions

    if (minPasswordLength !== undefined) {
      requirements.push({
        key: 'min-length',
        label: String(
          t('validation.passwordMinLength', {
            count: minPasswordLength,
            character: getStandardPluralLabel(
              t,
              i18n.language,
              'character',
              minPasswordLength
            ),
          })
        ),
        fulfilled: status.meetsMinPasswordLength === true,
      })
    }

    if (
      maxPasswordLength !== undefined &&
      status.meetsMaxPasswordLength === false
    ) {
      requirements.push({
        key: 'max-length',
        label: String(
          t('validation.passwordMaxLength', {
            count: maxPasswordLength,
            character: getStandardPluralLabel(
              t,
              i18n.language,
              'character',
              maxPasswordLength
            ),
          })
        ),
        fulfilled: false,
      })
    }

    if (containsLowercaseLetter) {
      requirements.push({
        key: 'lowercase',
        label: String(t('validation.passwordLowercaseLetter')),
        fulfilled: status.containsLowercaseLetter === true,
      })
    }

    if (containsUppercaseLetter) {
      requirements.push({
        key: 'uppercase',
        label: String(t('validation.passwordUppercaseLetter')),
        fulfilled: status.containsUppercaseLetter === true,
      })
    }

    if (containsNumericCharacter) {
      requirements.push({
        key: 'number',
        label: String(t('validation.passwordNumericCharacter')),
        fulfilled: status.containsNumericCharacter === true,
      })
    }

    if (containsNonAlphanumericCharacter) {
      requirements.push({
        key: 'special-character',
        label: String(t('validation.passwordNonAlphanumericCharacter')),
        fulfilled: status.containsNonAlphanumericCharacter === true,
      })
    }
  }

  const passwordsMatch =
    Boolean(password) && Boolean(repeatPassword) && password === repeatPassword
  if (!status && validating) {
    return (
      <RequirementsList aria-live="polite" aria-busy>
        <RequirementItem sx={{ ml: 2 }}>
          <CircularProgress size={42} />
        </RequirementItem>
      </RequirementsList>
    )
  }
  return (
    <RequirementsList aria-live="polite">
      {validationFailed && (
        <RequirementItem>
          <ErrorOutline color="error" fontSize="small" />
          <span>{t('validation.passwordValidationFailed')}</span>
        </RequirementItem>
      )}

      {requirements.map(({ key, label, fulfilled }) => (
        <RequirementItem key={key}>
          {fulfilled ? (
            <CheckCircleOutline color="success" fontSize="small" />
          ) : (
            <CancelOutlined color="error" fontSize="small" />
          )}
          <span>{label}</span>
        </RequirementItem>
      ))}

      <RequirementItem>
        {passwordsMatch ? (
          <CheckCircleOutline color="success" fontSize="small" />
        ) : (
          <CancelOutlined color="error" fontSize="small" />
        )}

        <span>{t('validation.passwordsMustMatch')}</span>
      </RequirementItem>
    </RequirementsList>
  )
}

export default PasswordRequirements
